import React from 'react';

import { useInterval } from '@lumapps/lumx-hooks/hooks/useInterval';

const POLL_INTERVAL = 1000;

export interface UseOAuthPopupOptions {
    /** URL substring signalling the flow finished; the (same-origin) popup is then closed. Omit for cross-origin/server-side callbacks. */
    closeKey?: string;
    /** `window.open` target. Defaults to `'_blank'`. */
    target?: string;
    /** `window.open` features string (e.g. `'width=600,height=600'`). */
    features?: string;
}

export interface UseOAuthPopup {
    /** Opens the popup at `url`; `onClose` fires when it closes. Returns `false` if the browser blocked it. */
    openPopup: (url: string, onClose?: () => void) => boolean;
    /** Whether the popup is currently open. */
    isOpen: boolean;
}

/**
 * Opens an OAuth popup, polls until it closes (or redirects to `closeKey`), then fires the open
 * call's `onClose`. Closes an orphaned popup on unmount — but does not fire `onClose` then.
 *
 * @example openPopup(authorizationUrl, refetchStatus);
 */
export const useOAuthPopup = ({ closeKey, target = '_blank', features }: UseOAuthPopupOptions = {}): UseOAuthPopup => {
    const popupRef = React.useRef<Window | null>(null);
    const onCloseRef = React.useRef<() => void>();
    const [isOpen, setIsOpen] = React.useState(false);

    const openPopup = React.useCallback(
        (url: string, onClose?: () => void) => {
            const popup = window.open(url, target, features);
            if (!popup) {
                return false;
            }
            popupRef.current = popup;
            onCloseRef.current = onClose;
            setIsOpen(true);
            return true;
        },
        [target, features],
    );

    useInterval(
        () => {
            const popup = popupRef.current;
            if (!popup) {
                return;
            }

            const finish = () => {
                popupRef.current = null;
                setIsOpen(false);
                onCloseRef.current?.();
            };

            if (popup.closed) {
                finish();
                return;
            }
            if (closeKey) {
                try {
                    // Cross-origin reads throw until the popup redirects back to our origin.
                    if (popup.location.href.includes(closeKey)) {
                        popup.close();
                        finish();
                    }
                } catch {
                    // Still on the provider's origin — keep polling.
                }
            }
        },
        isOpen ? POLL_INTERVAL : null,
    );

    React.useEffect(() => () => popupRef.current?.close(), []);

    return { openPopup, isOpen };
};
